/**
 * The agent's hands in the open editor.
 *
 * While a draft is open here, the server's docx tools ask this browser to do
 * text-level work instead of editing the server copy (`data-client-tool`
 * parts on the chat stream, answered over HTTP). The tracked change lands in
 * the page as it is proposed and the editor autosaves, so the attorney sees
 * the agent work where they are working.
 *
 * Rules the server relies on:
 * - Every locator is validated before anything is applied; a batch that cannot
 *   be placed applies nothing and reports the miss the way the engine would.
 * - A batch is fenced on `documentId` + `draftRevision` from the agent's last
 *   read. If the attorney typed since, a batch anchored on phrases is rebased
 *   onto the current text (each phrase found again); one that rewrites text as
 *   read (a paragraph rewrite, an offset range) is refused and the agent reads
 *   again.
 * - Anything the editor cannot do faithfully (a range holding an exhibit
 *   citation control, an ambiguous phrase) is handed back with `fallback` so
 *   the engine does it server-side.
 * - Citation content controls are never rewritten here; the engine owns them.
 */
import {
  createEditorBridge,
  executeToolCall
} from "@eigenpal/docx-editor-agents/bridge";

import type { DocxChange } from "../../api/types/draft";
import type { DraftSaveTrigger } from "../../api/drafts-v2";
import { ASSISTANT_PERSONA } from "../agents/types";
import { toDocxChange } from "../draftReview/liveChanges";

import { getOpenFile, nodeIdOfRelay } from "../officeEditor/openFiles";
import { getOpenDraft, type OpenDraftApi } from "./openDrafts";
import type {
  DocxEditorMode,
  DocxInlineControl,
  DocxReviewApi,
  DocxSurfaceHandle
} from "./surface";

export type ClientToolRequest = {
  id: string;
  tool: string;
  args: Record<string, unknown>;
  draftId: string;
};

export type ClientToolOutcome =
  | { ok: true; result: Record<string, unknown> }
  | { ok: false; error: string; detail?: Record<string, unknown> };

/** What each turn tells the server about the draft open here. */
export type OpenDraftPayload = {
  draft_id: string;
  title: string;
  dirty: boolean;
  read_only: boolean;
  mode: DocxEditorMode;
  revision: number;
  document_id: string;
  engine: string;
  selection: {
    paraId: string | null;
    text: string;
    before: string;
    after: string;
  } | null;
};

export const describeOpenDraft = (
  draftId: string | null | undefined
): OpenDraftPayload | null => {
  if (!draftId) return null;
  const draft = getOpenDraft(draftId);
  if (!draft) return null;
  const surface = draft.surface();
  const selection = surface?.getSelection() ?? null;
  return {
    draft_id: draftId,
    title: draft.title(),
    dirty: draft.isDirty(),
    read_only: draft.readOnly || !surface?.review,
    mode: draft.mode(),
    revision: draft.revision(),
    document_id: draftId,
    engine: surface?.engine ?? "eigenpal",
    selection:
      selection && selection.text.trim()
        ? {
            paraId: selection.paraId,
            text: selection.text,
            before: selection.before,
            after: selection.after
          }
        : null
  };
};

// --- Locators ----------------------------------------------------------------

type Locator = {
  paraId?: string;
  search?: string;
  occurrence?: number;
  start?: number;
  end?: number;
  story?: string;
  [key: string]: unknown;
};

/** A phrase the editor can act on: the paragraph and the exact words in it. */
type Target = { paraId: string; phrase: string; paragraphText: string };

class Refusal extends Error {
  readonly detail: Record<string, unknown>;

  constructor(message: string, detail: Record<string, unknown> = {}) {
    super(message);
    this.name = "Refusal";
    this.detail = detail;
  }
}

/** Ask the engine to do this instead (it can, faithfully). */
const fallback = (why: string) => new Refusal(why, { fallback: true });

const countOf = (haystack: string, needle: string): number => {
  if (!needle) return 0;
  let count = 0;
  let index = haystack.indexOf(needle);
  while (index !== -1) {
    count += 1;
    index = haystack.indexOf(needle, index + needle.length);
  }
  return count;
};

const asLocator = (value: unknown): Locator | null =>
  value && typeof value === "object" ? (value as Locator) : null;

/** Resolve a locator to a paragraph and a phrase that is unique inside it. */
const resolveTarget = (
  locator: Locator | null,
  review: DocxReviewApi,
  opIndex: number
): Target => {
  if (!locator) throw new Refusal(`op ${opIndex}: missing locator`);
  const unsupported = ["paragraphIndex", "table", "row", "col", "bookmark"];
  if (unsupported.some((key) => locator[key] != null))
    throw fallback(`op ${opIndex}: locator kind not addressable in the editor`);
  if (locator.story && locator.story !== "body")
    throw fallback(`op ${opIndex}: only the body is editable live`);

  let paraId = locator.paraId ? String(locator.paraId) : null;
  let paragraphText: string | null = null;
  let phrase: string;

  if (paraId) {
    paragraphText = review.getParagraphText(paraId);
    if (paragraphText === null)
      throw new Refusal(`op ${opIndex}: no paragraph with paraId ${paraId}`, {
        paraId
      });
    if (locator.search) {
      phrase = String(locator.search);
      const n = countOf(paragraphText, phrase);
      if (n === 0)
        throw new Refusal(
          `op ${opIndex}: no match for ${JSON.stringify(
            phrase
          )} in paragraph ${paraId}`,
          { paraId, search: phrase }
        );
      if (n > 1 || (locator.occurrence ?? 1) !== 1)
        throw fallback(`op ${opIndex}: the phrase repeats in the paragraph`);
    } else if (locator.start != null || locator.end != null) {
      const start = Math.max(0, Number(locator.start ?? 0));
      const end = Math.min(
        paragraphText.length,
        Number(locator.end ?? paragraphText.length)
      );
      phrase = paragraphText.slice(start, end);
      if (!phrase || countOf(paragraphText, phrase) !== 1)
        throw fallback(`op ${opIndex}: offset range is not a unique phrase`);
    } else {
      phrase = paragraphText;
    }
  } else if (locator.search) {
    const search = String(locator.search);
    const hits = review.findInDocument(search, { caseSensitive: true });
    const occurrence = locator.occurrence ?? 1;
    if (hits.length === 0)
      throw new Refusal(
        `op ${opIndex}: no match for ${JSON.stringify(search)}`,
        {
          search
        }
      );
    if (hits.length < occurrence)
      throw new Refusal(
        `op ${opIndex}: only ${hits.length} match(es) for ${JSON.stringify(
          search
        )}, occurrence ${occurrence} asked`,
        { search, matches: hits.length }
      );
    const hit = hits[occurrence - 1];
    paraId = hit.paraId;
    paragraphText = review.getParagraphText(paraId);
    if (paragraphText === null)
      throw new Refusal(`op ${opIndex}: paragraph vanished while locating`);
    phrase = hit.match;
    if (countOf(paragraphText, phrase) !== 1)
      throw fallback(`op ${opIndex}: the phrase repeats in the paragraph`);
  } else {
    throw new Refusal(`op ${opIndex}: locator needs paraId or search`);
  }

  return { paraId, phrase, paragraphText };
};

/** The engine owns citation controls; a phrase that swallows one goes to it. */
const guardControls = (
  target: Target,
  controls: readonly DocxInlineControl[],
  opIndex: number
) => {
  const inParagraph = controls.filter((c) => c.paraId === target.paraId);
  if (
    inParagraph.some((c) => c.text && target.phrase.includes(c.text)) ||
    (target.phrase === target.paragraphText && inParagraph.length > 0)
  ) {
    throw fallback(`op ${opIndex}: the range holds an exhibit citation`);
  }
};

// --- Minimal redline for a paragraph rewrite -------------------------------

const WORD_BOUNDARY = /\s/;

/**
 * The smallest phrase to replace so that a whole-paragraph rewrite reads as a
 * word-level redline: strip the common prefix and suffix at word boundaries,
 * then widen until the phrase is unique in the paragraph.
 */
export const minimalReplacement = (
  oldText: string,
  newText: string
): { search: string; replaceWith: string } => {
  if (oldText === newText) return { search: "", replaceWith: "" };
  let prefix = 0;
  while (
    prefix < oldText.length &&
    prefix < newText.length &&
    oldText[prefix] === newText[prefix]
  )
    prefix += 1;
  // Back up to a word boundary so we never split a word.
  while (prefix > 0 && !WORD_BOUNDARY.test(oldText[prefix - 1])) prefix -= 1;

  let suffix = 0;
  while (
    suffix < oldText.length - prefix &&
    suffix < newText.length - prefix &&
    oldText[oldText.length - 1 - suffix] ===
      newText[newText.length - 1 - suffix]
  )
    suffix += 1;
  while (suffix > 0 && !WORD_BOUNDARY.test(oldText[oldText.length - suffix]))
    suffix -= 1;

  const suffixStart = oldText.length - suffix;
  const middleNew = newText.slice(prefix, newText.length - suffix);

  // Widen until the phrase is unique in the paragraph (or is the whole
  // paragraph). A pure insertion mid-paragraph needs an anchoring word. The
  // widened parts are common text, so they read the same in both versions.
  let from = prefix;
  let to = suffixStart;
  const phrase = () => oldText.slice(from, to);
  while (
    (phrase() === "" || countOf(oldText, phrase()) > 1) &&
    (from > 0 || to < oldText.length)
  ) {
    if (from > 0) {
      from -= 1;
      while (from > 0 && !WORD_BOUNDARY.test(oldText[from - 1])) from -= 1;
    } else {
      to += 1;
      while (to < oldText.length && !WORD_BOUNDARY.test(oldText[to])) to += 1;
    }
  }
  const search = phrase();
  if (search === "") return { search: "", replaceWith: newText };
  return {
    search,
    replaceWith:
      oldText.slice(from, prefix) + middleNew + oldText.slice(suffixStart, to)
  };
};

// --- Planning and applying --------------------------------------------------

type Op = Record<string, unknown> & { op: string };

type Planned =
  | { kind: "propose"; paraId: string; search: string; replaceWith: string }
  | { kind: "comment"; paraId: string; search?: string; text: string }
  | { kind: "reply"; commentId: number; text: string }
  | { kind: "resolve"; commentId: number };

const planOp = (
  op: Op,
  index: number,
  review: DocxReviewApi,
  controls: readonly DocxInlineControl[]
): Planned => {
  const text = (value: unknown) => (value == null ? "" : String(value));
  switch (op.op) {
    case "replaceParagraphText": {
      const target = resolveTarget(asLocator(op.at), review, index);
      guardControls(target, controls, index);
      const { search, replaceWith } = minimalReplacement(
        target.paragraphText,
        text(op.text)
      );
      return { kind: "propose", paraId: target.paraId, search, replaceWith };
    }
    case "insertText": {
      const target = resolveTarget(asLocator(op.at), review, index);
      guardControls(target, controls, index);
      const inserted = text(op.text);
      const before = op.position === "before";
      return {
        kind: "propose",
        paraId: target.paraId,
        search: target.phrase,
        replaceWith: before
          ? inserted + target.phrase
          : target.phrase + inserted
      };
    }
    case "deleteText": {
      const target = resolveTarget(asLocator(op.range), review, index);
      guardControls(target, controls, index);
      return {
        kind: "propose",
        paraId: target.paraId,
        search: target.phrase,
        replaceWith: ""
      };
    }
    case "replaceText": {
      const locator =
        asLocator(op.range) ??
        (op.find != null
          ? {
              search: String(op.find),
              occurrence:
                op.occurrence == null ? undefined : Number(op.occurrence),
              ...(asLocator(op.scope) ?? {})
            }
          : null);
      if (op.regex || op.all || op.wholeWord)
        throw fallback(`op ${index}: regex / replace-all runs on the server`);
      const target = resolveTarget(locator, review, index);
      guardControls(target, controls, index);
      return {
        kind: "propose",
        paraId: target.paraId,
        search: target.phrase,
        replaceWith: text(op.replace)
      };
    }
    case "addComment": {
      const locator = asLocator(op.range) ?? asLocator(op.at);
      const target = resolveTarget(locator, review, index);
      const whole = target.phrase === target.paragraphText;
      return {
        kind: "comment",
        paraId: target.paraId,
        search: whole ? undefined : target.phrase,
        text: text(op.text)
      };
    }
    case "replyComment":
      return {
        kind: "reply",
        commentId: Number(op.commentId),
        text: text(op.text)
      };
    case "resolveComment":
      if (op.resolved === false)
        throw fallback(`op ${index}: reopening a comment runs on the server`);
      return { kind: "resolve", commentId: Number(op.commentId) };
    default:
      throw fallback(`op ${index}: ${op.op} is not a live op`);
  }
};

const applyPlanned = (
  planned: Planned,
  index: number,
  review: DocxReviewApi,
  author: string
) => {
  switch (planned.kind) {
    case "propose": {
      if (planned.search === "" && planned.replaceWith === "") return;
      const done = review.proposeChange({
        paraId: planned.paraId,
        search: planned.search,
        replaceWith: planned.replaceWith,
        author
      });
      if (!done)
        throw new Refusal(
          `op ${index}: the editor could not place the change (the words may already carry a revision)`,
          { paraId: planned.paraId }
        );
      return;
    }
    case "comment": {
      const id = review.addComment({
        paraId: planned.paraId,
        text: planned.text,
        author,
        search: planned.search
      });
      if (id === null)
        throw new Refusal(
          `op ${index}: the editor could not anchor the comment`,
          {
            paraId: planned.paraId
          }
        );
      return;
    }
    case "reply": {
      const id = review.replyToComment(planned.commentId, planned.text, author);
      if (id === null)
        throw new Refusal(
          `op ${index}: no comment with id ${planned.commentId}`
        );
      return;
    }
    case "resolve":
      review.resolveComment(planned.commentId);
      break;
    default:
      break;
  }
};

// --- Tools ---------------------------------------------------------------------

type LiveDraft = {
  api: OpenDraftApi;
  surface: DocxSurfaceHandle;
  review: DocxReviewApi;
};

const liveDraft = (draftId: string): LiveDraft => {
  const api = getOpenDraft(draftId);
  if (!api) throw fallback("the draft is not open in this editor anymore");
  const surface = api.surface();
  if (!surface || !surface.review)
    throw new Refusal("the editor is not ready to act", { fallback: true });
  if (api.readOnly)
    throw new Refusal("the draft is open read-only here", { fallback: true });
  return { api, surface, review: surface.review };
};

const saveAs = async (
  api: OpenDraftApi,
  trigger: DraftSaveTrigger
): Promise<{ saved: boolean; error?: string }> => {
  try {
    await api.save({ trigger });
    return { saved: true };
  } catch (error) {
    return {
      saved: false,
      error:
        error instanceof Error
          ? error.message
          : "The document changed in the editor but could not be saved."
    };
  }
};

/**
 * An op that finds its words in the current text, so it lands the same after
 * the attorney typed elsewhere: a `search` phrase, a whole-paragraph anchor
 * for an insertion, a comment id. A paragraph rewrite or a bare offset range
 * was computed against the text as read and could undo what they typed.
 */
export const rebaseable = (op: Op): boolean => {
  if (op.op === "replaceParagraphText") return false;
  return ["at", "range", "scope"].every((key) => {
    const locator = asLocator(op[key]);
    return (
      !locator ||
      locator.search != null ||
      (locator.start == null && locator.end == null)
    );
  });
};

/**
 * Refuse a batch read against another document, or against an older revision
 * unless every op re-anchors on the current text (then it is rebased: each
 * phrase is found again, and a phrase the attorney changed fails by name).
 * Returns whether the batch was rebased.
 */
const fence = (
  api: OpenDraftApi,
  draftId: string,
  args: Record<string, unknown>,
  ops: readonly Op[]
): boolean => {
  if (args.documentId != null && String(args.documentId) !== draftId)
    throw new Refusal(
      "that is not the document open here; read the current draft again",
      {
        documentId: draftId
      }
    );
  const expected = args.draftRevision;
  if (typeof expected !== "number" || expected === api.revision()) return false;
  if (ops.length > 0 && ops.every(rebaseable)) return true;
  throw new Refusal(
    "the draft changed in the editor since you read it, and this edit rewrites text as you read it; read it again",
    { draftRevision: api.revision() }
  );
};

const syncDraft = async (draftId: string): Promise<Record<string, unknown>> => {
  const { api } = liveDraft(draftId);
  if (!api.isDirty())
    return { saved: true, revision: api.revision(), documentId: draftId };
  const outcome = await saveAs(api, "manual_sections_edit");
  return { ...outcome, revision: api.revision(), documentId: draftId };
};

const readSelection = (draftId: string): Record<string, unknown> => {
  const { api, surface } = liveDraft(draftId);
  const selection = surface.getSelection();
  return {
    paraId: selection?.paraId ?? null,
    text: selection?.text ?? "",
    paragraphText: selection?.paragraphText ?? "",
    before: selection?.before ?? "",
    after: selection?.after ?? "",
    revision: api.revision()
  };
};

const applyOps = async (
  draftId: string,
  args: Record<string, unknown>
): Promise<Record<string, unknown>> => {
  const { api, surface, review } = liveDraft(draftId);
  const ops = Array.isArray(args.ops) ? (args.ops as Op[]) : [];
  const rebased = fence(api, draftId, args, ops);
  if (ops.length === 0) throw new Refusal("ops is empty");
  const author = String(args.author || ASSISTANT_PERSONA);
  const controls = surface.getInlineControls();

  // Plan everything first: a batch either places every op or none of them.
  const planned = ops.map((op, index) => planOp(op, index, review, controls));

  const before = new Set(review.listChanges().map((c) => c.id));
  const touched = new Set<string>();
  for (let index = 0; index < planned.length; index += 1) {
    applyPlanned(planned[index], index, review, author);
    const step = planned[index];
    if (step.kind === "propose" || step.kind === "comment")
      touched.add(step.paraId);
    if (index < planned.length - 1) {
      // Yield a frame so a batch paints one change at a time.
      // eslint-disable-next-line no-await-in-loop
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => resolve());
      });
    }
  }

  const after = review.listChanges();
  const changes: DocxChange[] = after
    .filter((c) => !before.has(c.id) && c.author === author)
    .map((c) => ({ ...toDocxChange(c), paraId: undefined }));
  const saved = await saveAs(
    api,
    (args.trigger as DraftSaveTrigger) || "agent_redline"
  );
  return {
    ...saved,
    revision: api.revision(),
    documentId: draftId,
    changes,
    touched: [...touched],
    pending_changes: after.length,
    ...(rebased ? { rebased: true } : {})
  };
};

const suggestChange = async (
  draftId: string,
  args: Record<string, unknown>
): Promise<Record<string, unknown>> => {
  const { api, surface, review } = liveDraft(draftId);
  const paraId = String(args.paraId || "");
  const search = String(args.search ?? "");
  const rebased = fence(api, draftId, args, [
    { op: "replaceText", range: { paraId, search } }
  ]);
  const editor = surface.editor();
  if (!editor) throw fallback("the editor is not ready to act");
  const replaceWith = String(args.replaceWith ?? "");
  if (!paraId) throw new Refusal("paraId is required");
  if (!search && !replaceWith)
    throw new Refusal("search and replaceWith are both empty");
  const author = String(args.author || ASSISTANT_PERSONA);
  const paragraphText = review.getParagraphText(paraId);
  if (paragraphText === null)
    throw new Refusal(`no paragraph with paraId ${paraId}`, { paraId });
  const phrase = search || paragraphText;
  guardControls(
    { paraId, phrase, paragraphText },
    surface.getInlineControls(),
    0
  );

  const before = new Set(review.listChanges().map((c) => c.id));
  const bridge = createEditorBridge(editor, author);
  const outcome = executeToolCall(
    "suggest_change",
    { paraId, search, replaceWith, author },
    bridge
  );
  if (!outcome.success) {
    throw fallback(outcome.error || "the editor could not place the change");
  }
  const after = review.listChanges();
  const changes: DocxChange[] = after
    .filter((c) => !before.has(c.id) && c.author === author)
    .map((c) => ({ ...toDocxChange(c), paraId: undefined }));
  const saved = await saveAs(
    api,
    (args.trigger as DraftSaveTrigger) || "agent_redline"
  );
  return {
    ...saved,
    revision: api.revision(),
    documentId: draftId,
    tracked_change: changes[0] ?? null,
    changes,
    pending_changes: after.length,
    ...(rebased ? { rebased: true } : {})
  };
};

// --- Office (xlsx / pptx) case files -------------------------------------------

/**
 * Server tool name → the verb the office editor's `executeAgentTool` speaks
 * (LegalWork's `office.agent_tool` control: read / write / replace_text /
 * save). The docx tools above are not listed: a Word case file registers in
 * the drafts registry under its relay id and takes the docx path.
 */
const OFFICE_TOOL_VERBS: Record<string, string> = {
  read_spreadsheet: "read",
  write_spreadsheet: "write",
  read_presentation: "read",
  replace_presentation_text: "replace_text",
  save_open_file: "save"
};

const runOfficeTool = async (
  nodeId: string,
  request: ClientToolRequest
): Promise<ClientToolOutcome> => {
  const api = getOpenFile(nodeId);
  if (!api)
    return {
      ok: false,
      error: "that file is not open in an editor here anymore",
      detail: { fallback: true }
    };
  if (request.tool === "save_open_file") {
    if (!api.isDirty())
      return {
        ok: true,
        result: {
          saved: true,
          revision: api.revision(),
          checksum: api.checksum()
        }
      };
    const saved = await api.save();
    return {
      ok: true,
      result: { saved, revision: api.revision(), checksum: api.checksum() }
    };
  }
  if (!api.executeAgentTool)
    return {
      ok: false,
      error: `the ${api.format} editor does not answer ${request.tool}`,
      detail: { fallback: true }
    };
  if (
    api.readOnly &&
    request.tool !== "read_spreadsheet" &&
    request.tool !== "read_presentation"
  )
    return {
      ok: false,
      error: "the file is open read-only here",
      detail: { fallback: true }
    };
  const verb = OFFICE_TOOL_VERBS[request.tool];
  if (!verb)
    return {
      ok: false,
      error: `the editor does not know the tool ${request.tool}`,
      detail: { fallback: true }
    };
  const outcome = await api.executeAgentTool(verb, request.args);
  if (!outcome.success)
    return { ok: false, error: outcome.error ?? "the editor refused" };
  return {
    ok: true,
    result: {
      data: outcome.data ?? null,
      saved: outcome.saved ?? false,
      revision: api.revision(),
      checksum: api.checksum()
    }
  };
};

/** Run one request against the open editor and describe what happened. */
export const runClientTool = async (
  request: ClientToolRequest
): Promise<ClientToolOutcome> => {
  const officeNodeId =
    request.tool in OFFICE_TOOL_VERBS ? nodeIdOfRelay(request.draftId) : null;
  if (officeNodeId) {
    try {
      return await runOfficeTool(officeNodeId, request);
    } catch (error) {
      return {
        ok: false,
        error:
          error instanceof Error
            ? error.message
            : "the editor failed to run this"
      };
    }
  }
  try {
    let result: Record<string, unknown>;
    switch (request.tool) {
      case "sync_draft":
        result = await syncDraft(request.draftId);
        break;
      case "read_selection":
        result = readSelection(request.draftId);
        break;
      case "apply_ops":
        result = await applyOps(request.draftId, request.args);
        break;
      case "suggest_change":
        result = await suggestChange(request.draftId, request.args);
        break;
      default:
        return {
          ok: false,
          error: `the editor does not know the tool ${request.tool}`,
          detail: { fallback: true }
        };
    }
    return { ok: true, result };
  } catch (error) {
    if (error instanceof Refusal)
      return { ok: false, error: error.message, detail: error.detail };
    return {
      ok: false,
      error:
        error instanceof Error ? error.message : "the editor failed to run this"
    };
  }
};
